import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import { getPetAssetKey, speciesChatbot } from './species';
import {
  createFlexMessageTemplate,
  createFlexContainer,
  createOpenGameAction,
} from './utils';
import {
  cardCollections,
  cardsCollectionSetId,
} from 'src/replicant/cards/cards.ruleset';
import { buildSender } from '../sender';

const cardThumbnailAssets = Object.entries(cardCollections).reduce<
  Record<string, string>
>((acc, [collectionId, collection]) => {
  for (const cardId of collection.cards) {
    acc[`cards/${cardId}/ja`] =
      `private/chatbot/cards/${cardsCollectionSetId}/${collectionId}/${cardId}_ja.png`;
    acc[`cards/${cardId}/en`] =
      `private/chatbot/cards/${cardsCollectionSetId}/${collectionId}/${cardId}_en.png`;
  }
  return acc;
}, {});

/**
 * Assets for cards
 */
export const cardsAssets = {
  'background/card_received/ja': 'private/chatbot/cards/card_received_ja.png',
  'background/card_received/en': 'private/chatbot/cards/card_received_en.png',
  'background/card_requested/ja': 'private/chatbot/cards/card_request_ja.png',
  'background/card_requested/en': 'private/chatbot/cards/card_request_en.png',

  ...cardThumbnailAssets,
};

/**
 * Create card received message.
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const cardReceivedMessage = createFlexMessageTemplate(
  SB.object({
    petName: SB.string(),
    species: SB.tuple(speciesChatbot),
    locale: SB.tuple(['en', 'ja']),
    cardId: SB.string(),
  }),
  (data, payload, api) => {
    const locale = data.locale;
    const altText = getLocaleConfig(locale).card.received.text;
    const normal = api.getAssetPath(getPetAssetKey(data.species));
    const cardId = data.cardId;

    const template = createFlexContainer(altText, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(altText, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath(`background/card_received/${locale}`),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(`cards/${cardId}/${locale}`),
                size: '3xl',
                position: 'relative',
                align: 'center',
                gravity: 'center',
              },
            ],
            width: '100%',
            height: '100%',
            position: 'absolute',
            justifyContent: 'center',
            alignItems: 'center',
          },
        ],
        paddingAll: '0px',
      },
    });

    // Add sender to message
    template['sender'] = buildSender({ name: data.petName, iconUrl: normal });

    return template;
  },
);

/**
 * Create card requested message.
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const cardRequestedMessage = createFlexMessageTemplate(
  SB.object({
    petName: SB.string(),
    species: SB.tuple(speciesChatbot),
    locale: SB.tuple(['en', 'ja']),
    cardId: SB.string(),
  }),
  (data, payload, api) => {
    const locale = data.locale;
    const altText = getLocaleConfig(locale).card.request.text;
    const normal = api.getAssetPath(getPetAssetKey(data.species));
    const cardId = data.cardId;

    const template = createFlexContainer(altText, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(altText, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath(`background/card_requested/${locale}`),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(`cards/${cardId}/${locale}`),
                size: '5xl',
                position: 'relative',
                align: 'center',
                gravity: 'center',
              },
            ],
            width: '100%',
            height: '78%',
            position: 'absolute',
            justifyContent: 'center',
            alignItems: 'center',
          },
        ],
        paddingAll: '0px',
      },
    });

    // Add sender to message
    template['sender'] = buildSender({ name: data.petName, iconUrl: normal });

    return template;
  },
);
